import cx from 'classnames'
import dayjs from 'dayjs'
import { map, set } from 'lodash/fp'
import memoize from 'memoize-one'
import type { CulturesPage_CulturePlate } from '~/api/backend/pages/culturesPageAPI'
import Table from '~/components/Table'
import TextWithOverflow from '~/components/TextWithOverflow'
import TinyMicroplate from '~/components/TinyMicroplate'
import {
  SUPPORTED_PLATE_DIMENSIONS,
  type supportedPlateFormats,
} from '~/components/TinyMicroplate.interface'
import { useFeatureFlag } from '~/core/featureFlags'
import { getCellConcentrationColor } from '~/pages/Monitor/utils/cellConcentrationColors'
import { convertWellCoordsToWellName } from '~/utils/microplate'
import { OVER_CONFLUENT_THRESHOLD } from '../Monitor/events/ViewModels/CultureViewModel'
import {
  DEAD_CULTURE_COLOR,
  EMPTY_CULTURE_COLOR,
  getConfluencyColor,
  OVER_CONFLUENT_COLOR,
} from '../Monitor/utils/confluencyColors'
import cs from './cultures_page.scss'

function renderTime(timestamp: string | null) {
  if (timestamp == null) {
    return <div className={cs.noData}>—</div>
  }

  const time = dayjs(timestamp)
  return <div className={cs.time}>{time.format('MMM D, YYYY h:mm A')}</div>
}

function renderCellConcentration(cellConcentration: number | null | undefined) {
  if (cellConcentration == null) {
    return <div className={cs.noData}>—</div>
  }

  return (
    <div className='font-semibold text-grey-dark'>
      {cellConcentration.toExponential(2)} cells/ml
    </div>
  )
}

function renderConfluence(confluence: number | null | undefined) {
  if (confluence == null) {
    return <div className={cs.noData}>—</div>
  }

  return (
    <div
      className={cs.confluence}
      style={{
        color: getConfluencyColor(confluence, true),
      }}
    >
      {Math.round(confluence)}%
    </div>
  )
}

function renderPlateStatus(isCheckedIn: boolean) {
  if (isCheckedIn == null) {
    return <div className={cs.noData}>—</div>
  }

  return (
    <div className={cx(cs.plateStatus, isCheckedIn && cs.checkedIn)}>
      {isCheckedIn ? 'Checked In' : 'Checked Out'}
    </div>
  )
}

function renderBarcode(barcode: string) {
  return (
    <TextWithOverflow className={cs.barcode} text={barcode} popoverContent={barcode} />
  )
}

const getWellCultureByWell = memoize((plate: CulturesPagePlate) =>
  Object.fromEntries(plate.wellCultures.map(wc => [wc.well, wc])),
)

function renderPlateContents(
  row: CulturesPagePlate,
  data: 'confluence' | 'cell_concentration',
) {
  const wellCultureByWell = getWellCultureByWell(row)
  const { rows, columns } = row.plateDimensions
  if (
    !SUPPORTED_PLATE_DIMENSIONS.some(
      supported => supported.rows === rows && supported.columns === columns,
    )
  ) {
    console.error(
      `Unsupported plate dimensions for TinyMicroplate: ${rows} x ${columns}`,
    )
    return null
  }

  const plateFormat = `wells_${rows * columns}`

  return (
    <TinyMicroplate
      plateFormat={plateFormat as supportedPlateFormats}
      highlights={[
        {
          colorRgbFn: (row, col) => {
            const wellName = convertWellCoordsToWellName(row, col)
            const wellCulture = wellCultureByWell[wellName]
            if (!wellCulture) {
              return EMPTY_CULTURE_COLOR
            }
            if (wellCulture.custom_culture_status.category === 'terminated') {
              return DEAD_CULTURE_COLOR
            }

            if (data === 'confluence') {
              const confluenceValue = wellCulture.confluence ?? 0
              if (confluenceValue > OVER_CONFLUENT_THRESHOLD) {
                return OVER_CONFLUENT_COLOR
              }
              return getConfluencyColor(confluenceValue)
            }

            const cellConcentration = wellCulture.cell_concentration ?? null
            return getCellConcentrationColor(cellConcentration)
          },
        },
      ]}
      shouldAnimate
    />
  )
}

interface CulturesPagePlate extends CulturesPage_CulturePlate {
  _link: string
}

export interface CulturesPagePlateTableProps {
  plates: CulturesPage_CulturePlate[]
  isLoadingMore?: boolean
  selectableProps?: {
    onRowsSelected: (rowsId: { [key: string]: boolean }) => void
    selectedRowIds: { [key: string]: boolean }
  }
}

export default function CulturesPagePlateTable(props: CulturesPagePlateTableProps) {
  const hackathon2025 = useFeatureFlag('hackathon2025')
  // Only add _link when NOT in selection mode
  const culturePagePlates = map(
    plate =>
      props.selectableProps
        ? plate // In selection mode, don't add _link
        : set('_link', `/plate/${plate.id}`, plate), // Normal mode, add _link for navigation
    props.plates,
  ) as CulturesPagePlate[]

  const tableColumns = [
    {
      name: 'Plate Contents',
      width: 150,
      render: (row: CulturesPagePlate) =>
        renderPlateContents(row, hackathon2025 ? 'cell_concentration' : 'confluence'),
    },
    {
      name: 'Barcode',
      width: 275,
      render: (row: CulturesPagePlate) => renderBarcode(row.barcode),
    },
    {
      name: 'Plate Status',
      width: 125,
      render: (row: CulturesPagePlate) => renderPlateStatus(row.isCheckedIn),
    },
    hackathon2025
      ? {
          name: 'Avg Cell Conc.',
          width: 175,
          render: (row: CulturesPagePlate) =>
            renderCellConcentration(row.averageCellConcentration),
          rightAlign: true,
          sortable: (row: CulturesPagePlate) => {
            // Sort by confluence value, treating null values as lowest (will appear at the top when sorted ascending)
            return row.averageCellConcentration ?? -Infinity
          },
        }
      : {
          name: 'Last Confluence',
          width: 175,
          render: (row: CulturesPagePlate) => renderConfluence(row.averageConfluence),
          rightAlign: true,
          sortable: (row: CulturesPagePlate) => {
            // Sort by confluence value, treating null values as lowest (will appear at the top when sorted ascending)
            return row.averageConfluence ?? -Infinity
          },
        },
    {
      name: 'First Checked In',
      width: 170,
      render: (row: CulturesPage_CulturePlate) => renderTime(row.firstCheckedInAt),
      rightAlign: true,
      sortable: (row: CulturesPage_CulturePlate) => {
        // Sort by timestamp, treating null values as earliest (will appear at the top when sorted ascending)
        return row.firstCheckedInAt
          ? new Date(row.firstCheckedInAt).getTime()
          : -Infinity
      },
    },
    {
      name: 'Last Checked Out',
      width: 170,
      render: (row: CulturesPage_CulturePlate) => renderTime(row.lastCheckedOutAt),
      rightAlign: true,
      sortable: (row: CulturesPage_CulturePlate) => {
        // Sort by timestamp, treating null values as earliest (will appear at the top when sorted ascending)
        return row.lastCheckedOutAt
          ? new Date(row.lastCheckedOutAt).getTime()
          : -Infinity
      },
    },
    {
      name: 'Last Generated At',
      width: 170,
      render: (row: CulturesPage_CulturePlate) => renderTime(row.lastGeneratedAt),
      rightAlign: true,
      sortable: (row: CulturesPage_CulturePlate) => {
        // Sort by timestamp, treating null values as earliest (will appear at the top when sorted ascending)
        return row.lastGeneratedAt ? new Date(row.lastGeneratedAt).getTime() : -Infinity
      },
    },
  ]

  // Handle row click to toggle selection when in selection mode
  const handleRowClick = props.selectableProps
    ? (row: CulturesPagePlate) => {
        const currentSelected = props.selectableProps?.selectedRowIds[row.id] || false
        const newSelectedRowIds = { ...props.selectableProps?.selectedRowIds }

        if (currentSelected) {
          // Remove from selection
          delete newSelectedRowIds[row.id]
        } else {
          // Add to selection
          newSelectedRowIds[row.id] = true
        }

        props.selectableProps?.onRowsSelected(newSelectedRowIds)
      }
    : undefined

  return (
    <Table<CulturesPagePlate>
      columns={tableColumns}
      data={culturePagePlates}
      rowKey='id'
      rowLeftRightPaddingVariant='rowPaddingNormal'
      headerTopBottomPaddingVariant='headerTopBottomPaddingEqual'
      heightSizing='default'
      stickyHeaders
      selectableProps={props.selectableProps}
      onRowClick={handleRowClick}
    />
  )
}
